<script setup lang="ts">
    import { v4 } from 'uuid';

    const props = withDefaults(defineProps<{
        bgColor?: string;
        dismissable?: boolean;
        id?: string;
        fixedTop?: boolean;
        zIndex?: number;
    }>(), {
        bgColor: 'bg-ngreen',
        dismissable: false,
        fixedTop: true,
        zIndex: 999,
    });

    const emit = defineEmits<{
        (e: 'dismiss', id?: string): void;
    }>();

    const isDismissed = ref(false);
    const finalId = computed(() => {
        return props?.id ?? (props.dismissable ? v4() : undefined);
    });

    const setDismissed = (key: string, value: string) => {
        try {
            if (process.client) {
                globalThis.localStorage.setItem(key, value);
            }
        } catch (error) {
            console.error('Error setting dismissed banner', error);
        }
    }

    const getIsDismissed = (key: string) => {
        try {
            if (process.client) {
                return globalThis.localStorage.getItem(key) === '1';
            }
            return false;
        } catch (error) {
            console.error('Error getting dismissed banner', error);
            return false;
        }
    }

    const dismiss = () => {
        emit('dismiss', finalId.value);
        if (props.dismissable && props.id) {
            setDismissed(`banner-${props.id}-dismissed`, '1');
            isDismissed.value = true;
        }
    }

    onMounted(() => {
        if (props.dismissable && props.id) {
            isDismissed.value = getIsDismissed(`banner-${props.id}-dismissed`);
        }
    });

</script>
<template>
    <div
        v-if="!isDismissed"
        data-test-id="banner"
        class="w-full h-10 shadow-ui text-white text-sm font-semibold flex items-center justify-center relative"
        :class="[{ 'top-0': fixedTop, 'fixed': fixedTop }, bgColor]"
        :style="{zIndex: zIndex}"
    >
        <slot />
        <button :data-test-id="`dismiss-banner-button-${finalId}`" class="absolute right-4" v-if="dismissable && props.id" @click="dismiss" title="Dismiss">
            <Icon name="heroicons:x-mark-20-solid" class="text-xl" />
        </button>
    </div>
</template>
